import React from 'react';
import { AlertTriangle, ArrowLeft, Send, Loader2 } from 'lucide-react';
import { useTranslation } from '../../contexts/I18nProvider';
import { TaValidationIssue } from '../../types/picking';

/**
 * TA-forhåndsvalidering ved avslutning (Teamwork 36369121). Delt mellom
 * plukk-direkteflyten (avslutt plukk) og pakkflyten (avslutt forsendelse).
 *
 * Vises når nShift-valideringen fant avvik (overvekt, manglende landkode, m.m.)
 * FØR bookingen gjøres — mens operatøren fortsatt kan gjøre noe med det.
 *
 * To veier videre, bevisst gjort vanskelige å forveksle (store, vertikalt stablede
 * knapper med tydelig avstand og ulik farge — trygt på små terminaler):
 *  - «Avbryt og rett» (primær): tilbake til skjermbildet operatøren sto i.
 *  - «Send likevel, og rett i nShift»: fullfører som før. SaveShipmentOnError sørger
 *    for at forsendelsen lander i nShift-innboksen der manglene kan kompletteres.
 *    Valget logges sporbart (hvem overstyrte hva).
 */
interface TaValidationDialogProps {
  isOpen: boolean;
  issues: TaValidationIssue[];
  /** «Avbryt og rett» — tilbake til skjermbildet, ingenting fullføres */
  onCancel: () => void;
  /** «Send likevel, og rett i nShift» — fullfør; forsendelsen kan rettes i nShift-innboksen */
  onProceed: () => void;
  busy?: boolean;
}

export const TaValidationDialog: React.FC<TaValidationDialogProps> = ({
  isOpen,
  issues,
  onCancel,
  onProceed,
  busy = false
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={22} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.taValidation.title', {}, 'Forsendelsen passerte ikke fraktvalideringen')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.picking.taValidation.prompt', {},
                'Transportsystemet fant følgende avvik. Rett dem nå, eller send likevel og fullfør forsendelsen i nShift-innboksen etterpå.')}
            </p>
          </div>
        </div>

        <ul className="mb-5 space-y-2">
          {issues.map((issue, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
            >
              <span className="font-medium flex-shrink-0">
                {issue.orderNo > 0
                  ? t('warehouse.modules.picking.taValidation.orderPrefix', { '0': String(issue.orderNo) }, 'Ordre {0}:')
                  : ''}
              </span>
              <span className="break-words">{issue.message}</span>
            </li>
          ))}
        </ul>

        {/* Bevisst vertikal stabel med stor avstand — de to valgene skal ikke kunne
            forveksles på et nettbrett. Primærvalget (trygt) øverst og mest fremtredende. */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 min-h-[52px] bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowLeft size={18} />
            {t('warehouse.modules.picking.taValidation.cancelAndFix', {}, 'Avbryt og rett')}
          </button>
          <button
            onClick={onProceed}
            disabled={busy}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 min-h-[52px] border-2 border-amber-400 bg-white text-amber-800 rounded-lg font-medium hover:bg-amber-50 disabled:opacity-50"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {t('warehouse.modules.picking.taValidation.proceedAnyway', {}, 'Send likevel, og rett i nShift')}
          </button>
        </div>
      </div>
    </div>
  );
};
